/**
 * useElementSize — measure an element's content-box size and re-render when it
 * changes, via a ResizeObserver. Lets charts and grids size to their container
 * instead of being hard-coded to fixed pixels or measuring window.innerWidth
 * (FXC-9004 / FXC-9006).
 *
 * SSR/test-safe: returns { width: 0, height: 0 } until measured, and no-ops
 * when ResizeObserver is unavailable.
 *
 * Usage:
 *   const [ref, { width, height }] = useElementSize<HTMLDivElement>();
 *   return <div ref={ref}>{width > 0 && <Chart width={width} />}</div>;
 */

import { useCallback, useEffect, useRef, useState } from "react";

export type ElementSize = { width: number; height: number };

export function useElementSize<T extends HTMLElement = HTMLDivElement>(): [
  (node: T | null) => void,
  ElementSize,
] {
  const [size, setSize] = useState<ElementSize>({ width: 0, height: 0 });
  const observerRef = useRef<ResizeObserver | null>(null);

  // Callback ref so we re-attach the observer if the node identity changes.
  const setRef = useCallback((node: T | null) => {
    observerRef.current?.disconnect();
    observerRef.current = null;

    if (!node || typeof ResizeObserver === "undefined") {
      return;
    }

    // Seed immediately so the first paint isn't a zero-size frame.
    const rect = node.getBoundingClientRect();
    setSize((prev) =>
      prev.width === rect.width && prev.height === rect.height
        ? prev
        : { width: rect.width, height: rect.height },
    );

    const observer = new ResizeObserver((entries) => {
      const entry = entries[0];
      if (!entry) return;
      const box = entry.contentRect;
      setSize((prev) =>
        prev.width === box.width && prev.height === box.height
          ? prev
          : { width: box.width, height: box.height },
      );
    });
    observer.observe(node);
    observerRef.current = observer;
  }, []);

  useEffect(() => {
    return () => {
      observerRef.current?.disconnect();
      observerRef.current = null;
    };
  }, []);

  return [setRef, size];
}
